import type { ThemeCssSize } from '../entity/schemas';
import { ThemeCssUnit } from '../enums/css.enum';
import { ThemeFontWeights } from '../enums/font-families.enum';
import type { ThemeFontFamilies } from '../enums/font-families.enum';
import { ThemeFontTags } from '../enums/font-tags.enum';

/**
 * The font vocabulary and the defaults that go with it.
 *
 * Seven tables across four files before this one, all split by which COLUMN of the same data they
 * held: the CSS stack per family, its display name, its weights; then a file each for the default
 * size, line height and letter spacing per TAG. Every one of them was keyed identically to its
 * siblings, so adding a family meant editing three tables and adding a tag meant editing three more
 * — with nothing to notice when one was missed.
 *
 * Two tables now, one per subject: a family, and a tag's defaults. `FONT_WEIGHT_VALUES` stays apart
 * because it is keyed by neither — it is the weight vocabulary's own numbers.
 */

export const FONT_WEIGHT_VALUES = {
    [ThemeFontWeights.light]: 300,
    [ThemeFontWeights.normal]: 400,
    [ThemeFontWeights.bold]: 700,
} as const;

/**
 * The three weight sets the shipped families come in. Named once because they repeat across 35 rows,
 * and the row is meant to be read at a glance — which combination a family offers is the only part
 * of it that varies unpredictably.
 */
const ALL_WEIGHTS = [ThemeFontWeights.light, ThemeFontWeights.normal, ThemeFontWeights.bold] as const;
const NORMAL_BOLD = [ThemeFontWeights.normal, ThemeFontWeights.bold] as const;
const NORMAL_ONLY = [ThemeFontWeights.normal] as const;

/** Everything the app knows about one family: how to show it, how to set it, what it comes in. */
export type FontFamilyDefinition = {
    /** What the picker shows. */
    name: string;
    /**
     * The CSS `font-family` stack, written out rather than derived from `name` + a category.
     *
     * Deriving it is tempting — most rows are exactly `${name}, sans-serif` — and it would be wrong
     * twice: `Exo 2` has to be QUOTED (an unquoted token starting with a digit is not a valid CSS
     * identifier, so the whole declaration would be dropped), and Playfair Display carries a stray
     * leading space that has been in the stored data for as long as the table has existed. A table
     * you can read beats a rule with two exceptions.
     */
    stack: string;
    /** The weights this family actually ships. Asking for another renders a synthesized fake. */
    weights: readonly ThemeFontWeights[];
};

/**
 * The families, one row each.
 *
 * Three parallel tables before this — name, stack and weights, keyed identically, edited together
 * and forgettable one at a time. `satisfies` is what keeps the keys honest now that they are plain
 * strings rather than computed enum members: a missing family or a misspelt one fails to compile,
 * which is what the bracket syntax was buying at the cost of twenty characters a row.
 */
export const FONT_FAMILIES = {
    pacifico: { name: 'Pacifico', stack: 'Pacifico, cursive', weights: NORMAL_ONLY },
    openSans: { name: 'Open Sans', stack: 'Open Sans, sans-serif', weights: ALL_WEIGHTS },
    lora: { name: 'Lora', stack: 'Lora, serif', weights: NORMAL_BOLD },
    roboto: { name: 'Roboto', stack: 'Roboto, sans-serif', weights: ALL_WEIGHTS },
    ptSerif: { name: 'PT Serif', stack: 'PT Serif, serif', weights: NORMAL_BOLD },
    ptSans: { name: 'PT Sans', stack: 'PT Sans, sans-serif', weights: NORMAL_BOLD },
    bitter: { name: 'Bitter', stack: 'Bitter, serif', weights: ALL_WEIGHTS },
    raleWay: { name: 'Raleway', stack: 'Raleway, sans-serif', weights: ALL_WEIGHTS },
    merriweather: { name: 'Merriweather', stack: 'Merriweather, serif', weights: ALL_WEIGHTS },
    firaSans: { name: 'Fira Sans', stack: 'Fira Sans, sans-serif', weights: ALL_WEIGHTS },
    jost: { name: 'Jost', stack: 'Jost, sans-serif', weights: ALL_WEIGHTS },
    ubuntu: { name: 'Ubuntu', stack: 'Ubuntu, sans-serif', weights: ALL_WEIGHTS },
    ibmPlexSans: { name: 'IBM Plex Sans', stack: 'IBM Plex Sans, sans-serif', weights: ALL_WEIGHTS },
    ibmPlexSerif: { name: 'IBM Plex Serif', stack: 'IBM Plex Serif, serif', weights: ALL_WEIGHTS },
    oswald: { name: 'Oswald', stack: 'Oswald, sans-serif', weights: ALL_WEIGHTS },
    playFairDisplay: { name: 'Playfair Display', stack: ' Playfair Display, serif', weights: NORMAL_BOLD },
    comfortaa: { name: 'Comfortaa', stack: 'Comfortaa, cursive', weights: ALL_WEIGHTS },
    sourceCodeProp: { name: 'Source Code Pro', stack: 'Source Code Pro, monospace', weights: ALL_WEIGHTS },
    manrope: { name: 'Manrope', stack: 'Manrope, sans-serif', weights: ALL_WEIGHTS },
    anonymousPro: { name: 'Anonymous Pro', stack: 'Anonymous Pro, monospace', weights: NORMAL_BOLD },
    balsamiqSans: { name: 'Balsamiq Sans', stack: 'Balsamiq Sans, cursive', weights: NORMAL_BOLD },
    lato: { name: 'Lato', stack: 'Lato, sans-serif', weights: ALL_WEIGHTS },
    montserrat: { name: 'Montserrat', stack: 'Montserrat, sans-serif', weights: ALL_WEIGHTS },
    inter: { name: 'Inter', stack: 'Inter, sans-serif', weights: ALL_WEIGHTS },
    commissioner: { name: 'Commissioner', stack: 'Commissioner, sans-serif', weights: ALL_WEIGHTS },
    mulish: { name: 'Mulish', stack: 'Mulish, sans-serif', weights: ALL_WEIGHTS },
    yanoneKaffeesatz: { name: 'Yanone Kaffeesatz', stack: 'Yanone Kaffeesatz, sans-serif', weights: ALL_WEIGHTS },
    philosopher: { name: 'Philosopher', stack: 'Philosopher, sans-serif', weights: NORMAL_BOLD },
    jura: { name: 'Jura', stack: 'Jura, sans-serif', weights: ALL_WEIGHTS },
    caveat: { name: 'Caveat', stack: 'Caveat, cursive', weights: NORMAL_BOLD },
    literata: { name: 'Literata', stack: 'Literata, serif', weights: ALL_WEIGHTS },
    unbounded: { name: 'Unbounded', stack: 'Unbounded, sans-serif', weights: ALL_WEIGHTS },
    cormorantGaramond: { name: 'Cormorant Garamond', stack: 'Cormorant Garamond, serif', weights: ALL_WEIGHTS },
    tektur: { name: 'Tektur', stack: 'Tektur, sans-serif', weights: NORMAL_BOLD },
    exo2: { name: 'Exo 2', stack: '"Exo 2", sans-serif', weights: ALL_WEIGHTS },
} as const satisfies Record<ThemeFontFamilies, FontFamilyDefinition>;

/** One tag's shipped defaults — the three sized properties a preset seeds it with. */
export type FontStyleDefaults = {
    fontSize: ThemeCssSize;
    lineHeight: ThemeCssSize;
    letterSpacing: ThemeCssSize;
};

const px = (value: number): ThemeCssSize => ({ value, unit: ThemeCssUnit.px });
const percent = (value: number): ThemeCssSize => ({ value, unit: ThemeCssUnit.percent });

/**
 * What each tag looks like before anyone edits it — the values every shipped font preset is built
 * from.
 *
 * **`pre` is absent on purpose**, which is why the record is `Partial`. It used to be spelled `''`
 * in each of the three files, an empty CSS string no stylesheet does anything with; an absent tag
 * says the same thing without pretending to be a value. `getThemeFontPresets` filters it out before
 * it ever reaches a lookup, so the gap costs nothing at the call site.
 *
 * Line height is a PERCENTAGE of the font size — headings are set tighter than body copy — and the
 * other two are pixels. Letter spacing is zero everywhere and still stated: presets emit
 * `letter-spacing: 0px` today, and dropping the entry would let the property inherit instead, which
 * is a rendering change and not a tidy-up.
 */
export const DEFAULT_FONT_STYLES = {
    [ThemeFontTags.leading]: { fontSize: px(24), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.big]: { fontSize: px(19), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.small]: { fontSize: px(13), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.quote]: { fontSize: px(18), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.signature]: { fontSize: px(14), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.p]: { fontSize: px(16), lineHeight: percent(140), letterSpacing: px(0) },
    [ThemeFontTags.h1]: { fontSize: px(36), lineHeight: percent(120), letterSpacing: px(0) },
    [ThemeFontTags.h2]: { fontSize: px(28), lineHeight: percent(120), letterSpacing: px(0) },
    [ThemeFontTags.h3]: { fontSize: px(22), lineHeight: percent(120), letterSpacing: px(0) },
    [ThemeFontTags.h4]: { fontSize: px(18), lineHeight: percent(120), letterSpacing: px(0) },
    [ThemeFontTags.button]: { fontSize: px(16), lineHeight: percent(120), letterSpacing: px(0) },
} as const;

/**
 * One tag's defaults, or `undefined` for a tag the table has no row for.
 *
 * The table is `as const`, so its key type is the eleven tags it actually holds — indexing it with a
 * `ThemeFontTags` that might be `pre` is a type error at every call site. Widened once here instead:
 * the gap is this table's business, not its readers'.
 */
export function fontStyleDefaults(tag: ThemeFontTags): FontStyleDefaults | undefined {
    return (DEFAULT_FONT_STYLES as Partial<Record<ThemeFontTags, FontStyleDefaults>>)[tag];
}
